// src/components/ObjectHierarchy.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Business as BusinessIcon,
  Apartment as ApartmentIcon,
  Settings  as PrecisionIcon,
  Memory as MemoryIcon,
  QrCode as QrCodeIcon,
  Nfc as NfcIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  fetchNodeHierarchy,
  fetchNodeDetail,
  searchNodes,
  toggleNodeExpansion,
  clearSearchResults,
  clearError,
} from '../store/slices/nodesSlice';
import { NodeDto, ObjectHierarchyProps } from '../types/nodes';

const ObjectHierarchy: React.FC<ObjectHierarchyProps> = ({
  onNodeSelect,
  onNodeExpand,
  maxDepth = 10,
  showSearch = true,
  showActions = true,
}) => {
  const dispatch = useAppDispatch();
  const {
    hierarchy,
    selectedNode,
    expandedNodes,
    searchResults,
    loading,
    error,
    metadata,
  } = useAppSelector((state) => state.nodes);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchMode, setSearchMode] = useState<'name' | 'barcode' | 'nfc'>('name');

  useEffect(() => {
    dispatch(fetchNodeHierarchy({ maxDepth }));
  }, [dispatch, maxDepth]);

  const handleSearch = (value: string) => {
  setSearchTerm(value);
  
  if (!value.trim()) {
    dispatch(clearSearchResults());
    return;
  }

  const searchParams: any = {}; // DODAJ typ any
  switch (searchMode) {
    case 'barcode':
      searchParams.barCodeId = value;
      break;
    case 'nfc':
      searchParams.nfcId = value;
      break;
    default:
      searchParams.name = value;
  }

  dispatch(searchNodes(searchParams));
};

  const handleNodeSelect = (nodeId: string) => {
  const numericNodeId = parseInt(nodeId);
  dispatch(fetchNodeDetail(numericNodeId));
  const foundNode = findNodeById(hierarchy, numericNodeId);
  if (foundNode) { // DODAJ sprawdzenie
    onNodeSelect?.(foundNode);
  }
};

  const handleNodeToggle = (nodeIds: string[]) => {
    const currentExpanded = [...expandedNodes];
    const newExpanded = nodeIds.map(id => parseInt(id));
    
    // Find which node was toggled
    const added = newExpanded.find(id => !currentExpanded.includes(id));
    const removed = currentExpanded.find(id => !newExpanded.includes(id));
    
    if (added) {
      dispatch(toggleNodeExpansion(added));
      onNodeExpand?.(added, true);
    }
    
    if (removed) {
      dispatch(toggleNodeExpansion(removed));
      onNodeExpand?.(removed, false);
    }
  };

  const getNodeIcon = (node: NodeDto) => {
    const level = node.level;
    
    if (level === 0) return <BusinessIcon />;
    if (level === 1) return <ApartmentIcon />;
    if (level === 2) return <PrecisionIcon />;
    return <MemoryIcon />;
  };

  const getNodeColor = (node: NodeDto) => {
    const level = node.level;
    const colors = ['#1976d2', '#388e3c', '#f57c00', '#d32f2f'];
    return colors[level] || '#666';
  };

  const renderTreeItem = (node: NodeDto): React.ReactElement => {
    const nodeId = node.nodeId.toString();
    const hasQrNfc = node.barCodeId || node.nfcId;
    
    return (
      <TreeItem
        key={nodeId}
        itemId={nodeId}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
            <Box sx={{ color: getNodeColor(node), mr: 1 }}>
              {getNodeIcon(node)}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 'medium', flexGrow: 1 }}>
              {node.name}
            </Typography>
            {hasQrNfc && (
              <Box sx={{ ml: 1 }}>
                {node.barCodeId && (
                  <Tooltip title={`QR: ${node.barCodeId}`}>
                    <QrCodeIcon sx={{ fontSize: 16, color: 'primary.main', mr: 0.5 }} />
                  </Tooltip>
                )}
                {node.nfcId && (
                  <Tooltip title={`NFC: ${node.nfcId}`}>
                    <NfcIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                  </Tooltip>
                )}
              </Box>
            )}
            {node.externalId && (
              <Chip
                label={node.externalId}
                size="small"
                variant="outlined"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        }
        sx={{
          '& .MuiTreeItem-content': {
            padding: '4px 8px',
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '&.Mui-selected': {
              backgroundColor: 'primary.light',
              '&:hover': {
                backgroundColor: 'primary.light',
              },
            },
          },
          '& .MuiTreeItem-label': {
            fontSize: '0.875rem',
          },
        }}
      >
        {node.children.map(renderTreeItem)}
      </TreeItem>
    );
  };

  const renderSearchResults = () => {
    if (!searchTerm || searchResults.length === 0) return null;

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Typography variant="subtitle2" gutterBottom>
            Search Results ({searchResults.length})
          </Typography>
          {searchResults.map((node) => (
            <Box
              key={node.nodeId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1,
                borderRadius: 1,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
              onClick={() => handleNodeSelect(node.nodeId.toString())}
            >
              <Box sx={{ color: getNodeColor(node), mr: 1 }}>
                {getNodeIcon(node)}
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {node.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {node.path}
                </Typography>
              </Box>
              {(node.barCodeId || node.nfcId) && (
                <Box>
                  {node.barCodeId && <QrCodeIcon sx={{ fontSize: 16, mr: 0.5 }} />}
                  {node.nfcId && <NfcIcon sx={{ fontSize: 16 }} />}
                </Box>
              )}
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  };

  const findNodeById = (nodes: NodeDto[], id: number): NodeDto | undefined => {
    for (const node of nodes) {
      if (node.nodeId === id) return node;
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
    return undefined;
  };

  if (loading && hierarchy.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {error && (
        <Alert 
          severity="error" 
          onClose={() => dispatch(clearError())}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {showSearch && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={`Search by ${searchMode}...`}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchTerm('');
                      dispatch(clearSearchResults());
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            {['name', 'barcode', 'nfc'].map((mode) => (
              <Chip
                key={mode}
                label={mode.toUpperCase()}
                size="small"
                variant={searchMode === mode ? 'filled' : 'outlined'}
                onClick={() => setSearchMode(mode as any)}
                sx={{ textTransform: 'capitalize' }}
              />
            ))}
          </Box>
        </Box>
      )}

      {renderSearchResults()}

      {hierarchy.length > 0 ? (
        <SimpleTreeView
      expandedItems={expandedNodes.map(String)}
      selectedItems={selectedNode?.nodeId.toString() || ''}
      onSelectedItemsChange={(event, itemId) => {
    if (itemId && typeof itemId === 'string') {
      handleNodeSelect(itemId);
    }
  }}
  onExpandedItemsChange={(event, itemIds) => {
    if (Array.isArray(itemIds)) {
      handleNodeToggle(itemIds);
    }
  }}
  sx={{
    flexGrow: 1,
    overflowY: 'auto',
    '& .MuiTreeItem-root': {
      '& .MuiTreeItem-content': {
        borderRadius: 1,
      },
    },
  }}
>
  {hierarchy.map(renderTreeItem)}
</SimpleTreeView>
      ) : (
        !loading && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
            No objects available or insufficient permissions
          </Typography>
        )
      )}

      {metadata.totalCount && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Total objects: {metadata.totalCount}
        </Typography>
      )}
    </Box>
  );
};

export default ObjectHierarchy;